import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import {
  GetAvailableSavingsProducts /* SetSavingProductsState optional action */,
} from '../actions/savings-account.action';
import { SavingsAccountService } from '../services/savings-account-service/savings-account.service';
import { AvailableSavingsProductType } from '../services/savings-account-service/savings-account.type';
import { ApiActions, UpdateApiIsLoadingAction } from './api-loading.state';
import { SavingsProductsCategory, SavingsProductType } from '@finxone-platform/shared/sys-config-types';

export interface SavingsProductsStateModel {
  availableProducts: AvailableSavingsProductType[];
  availableProductsForRenewal: AvailableSavingsProductType[];
}

@State<SavingsProductsStateModel>({
  name: 'savingsProducts',
  defaults: {
    availableProducts: [],
    availableProductsForRenewal: [],
  },
})
@Injectable()
export class SavingsProductsState {
  constructor(private readonly savingsAccountService: SavingsAccountService) {}

  @Selector()
  static getState(state: SavingsProductsStateModel) {
    return state;
  }

  @Selector()
  static getAvailableProducts(state: SavingsProductsStateModel) {
    return state.availableProducts;
  }

  static getProduct(productCode: string) {
    return createSelector([SavingsProductsState], (state: SavingsProductsStateModel) => {
      return state.availableProducts.find((product) => product.productCode === productCode?.toString());
    });
  }

  @Selector()
  static getAvailableProductsForRenewal(state: SavingsProductsStateModel) {
    return state.availableProductsForRenewal;
  }

  static getRenewalProduct(productCode: string) {
    return createSelector([SavingsProductsState], (state: SavingsProductsStateModel) => {
      return state.availableProductsForRenewal.find(
        (product) => product.productCode === productCode?.toString(),
      );
    });
  }

  @Action(GetAvailableSavingsProducts)
  getAvailableSavingsProducts(
    ctx: StateContext<SavingsProductsStateModel>,
    action: GetAvailableSavingsProducts,
  ) {
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsProduct, true));
    return this.savingsAccountService.getAvailableProducts(action?.payload).pipe(
      tap((products) => {
        if (action?.payload?.category === SavingsProductsCategory.RENEWAL) {
          this.patchRenewalProducts(ctx, products, action?.payload);
        } else {
          this.patchProducts(ctx, products, action?.payload);
        }
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsProduct, false));
      }),
      catchError((err) => {
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsProduct, false));
        return throwError(() => err);
      }),
    );
  }

  private patchRenewalProducts(
    ctx: StateContext<SavingsProductsStateModel>,
    products: SavingsProductType[],
    payload: GetAvailableSavingsProducts['payload'],
  ) {
    const state = ctx.getState();
    if (payload?.page && payload.page > 1) {
      ctx.patchState({
        availableProductsForRenewal: [...state.availableProductsForRenewal, ...products],
      });
    } else {
      ctx.patchState({ availableProductsForRenewal: products });
    }
  }

  private patchProducts(
    ctx: StateContext<SavingsProductsStateModel>,
    products: SavingsProductType[],
    payload: GetAvailableSavingsProducts['payload'],
  ) {
    const state = ctx.getState();
    if (payload?.page && payload.page > 1) {
      ctx.patchState({
        availableProducts: [...state.availableProducts, ...products],
      });
    } else {
      ctx.patchState({ availableProducts: products });
    }
  }
}
